import * as React from 'react';
import { PageWrapper } from 'Shared/PageWrapper';
import styles from './PrivacyPolicyPage.module.css';
import ExpandableSection from 'Shared/ExpandableSection/ExpandableSection';

const PrivacyPolicyPage = (): React.ReactElement => (
    <PageWrapper pageTitle="Privacy Policy">
        <ExpandableSection title="Privacy Policy">
            <section className={styles.PrivacyPage}>
                <div>
                    <h2>Privacy Policy</h2>
                </div>

                <div>
                    <p>
                        Welcome to the Privacy Policy (this &quot;Privacy Policy&quot;) of ID Plans and its affiliates
                        (collectively, &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). This Privacy Policy applies to any of
                        the websites or online services owned, licensed, or operated by ID Plans, including{' '}
                        <a href="https://www.idplans.com/" target="_blank" rel="noreferrer noopener">
                            https://www.idplans.com/
                        </a>{' '}
                        and{' '}
                        <a href="https://tenant.idplans.com/" target="_blank" rel="noreferrer noopener">
                            https://tenant.idplans.com/
                        </a>
                        (collectively, our &quot;Websites&quot;). <br />
                        <br />
                        This Privacy Policy sets forth ID Plan&apos;s practices regarding our collection, use and disclosure of
                        personal information and other data that you may provide to us, including through our Websites or in
                        e-mail or other electronic communication you send to us. It also describes the privacy rights you may
                        have with respect to your personal information, and your responsibilities and obligations. By providing
                        us with personal information, or by accessing or submitting information through our Websites, you agree
                        to the terms and conditions of this Privacy Policy without limitation or qualification. This Privacy
                        Policy should be read in conjunction with the <a href="/terms-of-service">Terms and Conditions of Use</a>
                        , which is incorporated into, and forms an integral part of, this Privacy Policy.
                    </p>
                </div>

                <div>
                    <p>
                        This Privacy Policy applies to personal information that ID Plans receives during our business operations
                        as a &quot;data controller&quot; - which means we determine the purpose and means of processing such
                        personal information. For purposes of this Privacy Policy, the term &quot;personal information&quot;
                        refers to any information that, either alone or in conjunction with other data, can be used to
                        distinguish or identify a particular individual and is subject to, or otherwise afforded protection
                        under, a law, statute, or regulation governing data privacy or security.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>1. What Information Do We Collect?</strong>
                    </p>
                </div>

                <div>
                    <p>
                        We collect personal information and other data under a broad range of circumstances. Generally, we
                        collect the following types of personal information and data directly from you:
                    </p>
                </div>

                <div>
                    <div>
                        <ul>
                            <li>
                                <p>
                                    <strong>Identity data</strong>, such as your name, title, company/organization name, e-mail
                                    address, telephone and fax numbers, physical address (including street, city, state, postal
                                    code, and/or country), and date of birth.
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>Registration data</strong>, such as information provided by you when you register for
                                    an account to use our Websites, including usernames and passwords.
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>Business contact data</strong>, such as information related to other employees,
                                    owners, directors, officers, or contractors of a third-party organization (e.g., business,
                                    company, partnership, sole proprietorship, nonprofit, or government agency) with whom we may
                                    conduct, or possibly conduct, business activities.
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>Marketing and communications data</strong>, including your marketing preferences and
                                    your subscriptions to our publications.
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>Transaction data</strong>, including orders for our products and services and details
                                    of payments to and from you, including purchase history and preferences and (limited) payment
                                    card information.
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>

                <div>
                    <div>
                        <ul>
                            <li>
                                <p>
                                    <strong>Leasing and Property Management Services data</strong>, including social security
                                    numbers, driver&apos;s license numbers and financial information, bank account numbers, and
                                    credit/debit card numbers, which is information needed for leasing and underwriting activity,
                                    credit and background checks, compliance with loan covenants, insurance claims management,
                                    and the sale of shopping centers.
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>Investment data about your investments with us</strong>, including investment amounts
                                    and information provided by completing subscription documents for deals closed, your personal
                                    investment entities and their co-owners and beneficiaries, and tax related information in
                                    connection with your investments with us.
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>Your feedback</strong>, including feedback from you about our Websites as well as our
                                    shopping centers and services generally, which may include data gathered from any of our
                                    surveys in which you participate.
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>Technical and website usage data</strong> that we automatically collect from
                                    individuals who visit or access our Websites, such as a user&apos;s Internet Protocol
                                    (&quot;IP&quot;) address, browser and computer type, access time, the webpage from which you
                                    came, and the specific webpage(s) they access.
                                </p>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p>
                            When you do not provide personal information to us (as requested), we may not be able to provide you
                            the services you need or complete a transaction, and you agree that we will not be liable or
                            otherwise responsible for any actions resulting therefrom.
                        </p>
                    </div>
                </div>

                <div>
                    <p>
                        <strong>2. Whose Personal Information Do We Collect</strong>
                    </p>
                </div>

                <div>
                    <p>
                        We collect personal information from a range of individuals in the context of our business activities,
                        including from the following: our tenants; stockholders; partners; individuals that use, or otherwise
                        access, our Websites; individuals who visit our centers, premises or facilities; vendors, service
                        providers, contractors and similar types of workers; individuals related to, or otherwise affiliated
                        with, our employees; job applicants; and other individuals who contact us by any means.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>3. Children Under the Age of 13</strong>
                    </p>
                </div>

                <div>
                    <p>
                        This site is not directed to children and we do not knowingly solicit information from any child under
                        the age of 13. If you are under 13, please do not provide us with any personal information.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>4. How Your Personal Information is Used</strong>
                    </p>
                </div>

                <div>
                    <p>
                        We may use your personal information to provide you with information or services that you have
                        specifically requested or to fulfill any other purpose for which you provide it. We may also use it to
                        communicate with you about topics in which you may be interested, to provide new information regarding
                        our business, to notify you about changes to our Websites, for any other purpose we describe when you
                        provide personal information or in any other manner with your consent.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>5. Do Not Track</strong>
                    </p>
                </div>

                <div>
                    <p>
                        We do not currently take steps to respond to browsers&apos; &quot;Do Not Track&quot; signals as no
                        uniform standard to respond to such signals has been developed at this time.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>6. Disclosure of Your Information</strong>
                    </p>
                </div>

                <div>
                    <p>
                        We do not sell, lease, or rent your personal information to third parties for profit or any other
                        valuable consideration. However, we do share or otherwise disclose your personal information with
                        selected third parties in accordance with applicable law, including under the following circumstances:
                    </p>
                </div>

                <div>
                    <div>
                        <ul>
                            <li>
                                <p>It may be shared within our company, and with our subsidiaries and affiliates.</p>
                            </li>
                            <li>
                                <p>
                                    It may be collected by a third-party vendor that acts as a hosting service for our Websites
                                    or who provides use other business services.
                                </p>
                            </li>
                            <li>
                                <p>It may be retained by an outside vendor for storage purposes.</p>
                            </li>
                            <li>
                                <p>
                                    We may provide your information to a third party if you specifically agree that we may do so.
                                </p>
                            </li>
                            <li>
                                <p>
                                    We may provide your information to third party service providers, contractors, business
                                    partners or other third parties that we use to support our business, including for assistance
                                    with sending information by email, mail or other means.
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>

                <div>
                    <div>
                        <ul>
                            <li>
                                <p>We may use it to fulfill any purpose for which you provide it.</p>
                            </li>
                            <li>
                                <p>
                                    We may send one or more &quot;cookies&quot; to you in order to improve the quality of our
                                    service, including for tracking user trends and preferences.
                                </p>
                            </li>
                            <li>
                                <p>
                                    We may need to disclose your personal information when required by law or if we have a good
                                    faith belief that such action is necessary to protect our rights or to comply with a court
                                    order or subpoena, to cooperate with investigations by law enforcement, regulatory
                                    authorities, or governmental entities or to participate or cooperate with a judicial
                                    proceeding or arbitration.
                                </p>
                            </li>
                            <li>
                                <p>
                                    We may disclose your personal information if we believe disclosure is necessary or
                                    appropriate to protect the rights, property, safety, or interests of us, our stockholders,
                                    our business partners, or others.
                                </p>
                            </li>
                            <li>
                                <p>
                                    We may disclose Personal Information about our neighbors to credit bureaus, banks and
                                    lenders, insurance companies and adjusters, purchasers of shopping centers, and other service
                                    providers.
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>

                <div>
                    <div>
                        <ul>
                            <li>
                                <p>
                                    In a merger, acquisition by another company, a sale of all or a portion of our assets,
                                    bankruptcy, liquidation, or other similar transactions, your personal information will, in
                                    most instances, be transferred to the control of a third party.
                                </p>
                            </li>
                            <li>
                                <p>
                                    We may also share your personal information for any other purpose we describe when you
                                    provide the information or in any other manner with your consent.
                                </p>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p>
                            We may remove personal identifiers from the personal information you provide to us and maintain it in
                            a de-identified and aggregate form. We may combine such de-identified information with other
                            information to provide anonymous, aggregated statistical information for use by us, our business
                            partners or our service providers to improve our information, centers, products and services. We may
                            disclose aggregated information about our users without restriction.
                        </p>
                    </div>
                    <div>
                        <p>
                            <strong>7. Cookie Data</strong>
                        </p>
                    </div>
                    <div>
                        <p>
                            Our Websites may use &quot;cookies&quot; or other tracking technology. Cookies are small pieces of
                            program code that reside on your computer. By showing how and when visitors use our Websites, cookies
                            help us identify how many unique users visit us, and track user interests, trends, and patterns, all
                            of which helps us make our Websites more useful. You can opt-out of the use of cookies by having your
                            browser disable cookies or warn you before accepting a cookie. Look for the cookie options in your
                            browser in the options or preferences menu. If you disable cookies in your browser, however, you may
                            not be able to take advantage of certain of the features or functions of, or information, products
                            and service available, via our Websites. Any anonymous data that we collect through the use of
                            cookies or other tracking technology may be used by us and disclosed by us to third parties to
                            improve this Website and to better understand the needs of our users.
                        </p>
                    </div>
                </div>

                <div>
                    <p>
                        <strong>8. Data Retention and Localization</strong>
                    </p>
                </div>

                <div>
                    <p>
                        The period during which we retain your personal information varies depending on the purpose for the data
                        processing. For example, we retain personal information needed to provide you with products and services,
                        to facilitate transactions you have requested, or to engage in marketing activities, and for so long as
                        necessary to defend our legal or business interests. In all other cases, we retain your personal
                        information for as long as is needed to fulfill the purposes outlined in this Privacy Policy. We are
                        based in the United States and the personal information that we collect and process is retained and
                        stored in the United States. Please be aware that the United States may not provide the same level of
                        protection of personal information as in your country, state, or other jurisdiction of residence or
                        nationality, and when transferred to the United States, your personal information may be accessible by,
                        or otherwise made available to, government authorities and officials pursuant to judicial and/or
                        administrative orders, decrees, and demands, and/or other domestic laws, statutes, and regulations,
                        applicable in the United States. By continuing to provide us such information, you hereby consent to your
                        personal information being transferred to, and stored in, the United States.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>9. Information Security</strong>
                    </p>
                </div>

                <div>
                    <p>
                        We seek to protect the security of your personal information and use a variety of security technologies
                        and procedures to help protect your personal information from unauthorized access, use or disclosure.
                        HOWEVER, NO INFORMATION SYSTEM CAN BE FULLY SECURE AND WE CANNOT GUARANTEE THE ABSOLUTE SECURITY OF YOUR
                        PERSONAL INFORMATION. MOREOVER, WE ARE NOT RESPONSIBLE FOR THE SECURITY OF PERSONAL INFORMATION YOU
                        TRANSMIT TO OUR WEBSITES AND/OR THE SERVICES OVER NETWORKS THAT WE DO NOT CONTROL, INCLUDING THE INTERNET
                        AND WIRELESS NETWORKS, AND YOU PROVIDE US WITH ANY PERSONAL INFORMATION AND DATA AT YOUR OWN RISK. TO THE
                        EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE OR OTHERWISE RESPONSIBLE FOR ANY DATA INCIDENTS THAT MAY
                        COMPROMISE THE CONFIDENTIALITY, INTEGRITY, OR SECURITY OF YOUR PERSONAL INFORMATION. The safety and
                        security of your personal information also depends on you. Where we have given you (or where you have
                        chosen) a username and password to access our Websites, you are responsible for maintaining the security
                        and confidentiality of those credentials and not revealing them to others. You must contact us
                        immediately if you have reason to believe that your username or password to our Websites has been
                        compromised. In the event we are required by law to notify you of a data security incident or compromise,
                        you hereby agree and consent to receive such notification via electronic communication, including email
                        communication.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>10. Third Party Privacy Policies</strong>
                    </p>
                </div>

                <div>
                    <p>
                        As a resource to you, this site may include links to third party websites. Our Privacy Policy does not
                        apply to such websites, and you should contact them directly for information regarding their privacy
                        policies.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>11. Employment Applications and Talent Management</strong>
                    </p>
                </div>

                <div>
                    <p>
                        As part of our recruitment and talent management process, we collect personal information with respect to
                        individuals who are interested in working for us. In this context, we collect employment and application
                        data, such as contact information (including, without limitation, name, title, residential or postal
                        address, telephone number, and personal email address); information in a curriculum vitae, resume, cover
                        letter, or similar documentation; details regarding the type of employment sought, willingness to
                        relocate, job compensation and benefit preferences; health data (e.g., medical conditions); information
                        related to your background, education, criminal record, credit history and similar data; information
                        provided about or by your references or other third parties related to your employment history, skills,
                        qualifications, or education; and, information related to previous applications to us or previous
                        employment history with us. When permitted by law, we may collect information about your race and
                        ethnicity to assist with our diversity and inclusion programs. We use this information for the following
                        purposes: to identify and evaluate job applicants; to verify your information; to complete employment,
                        education, background and reference checks; to communicate with you about the recruitment process and
                        your application; to comply with our legal, judicial, regulatory, administrative, or other corporate
                        requirements; to analyze and improve our application and recruitment process; to accommodate individuals
                        who may have specialized needs during the employment process; and to protect the rights, interests, and
                        property of our business, other job applicants, employees (whom we refer to as our
                        &quot;associates&quot;), or the public, as required, or permitted, by law. We may also use this
                        employment and application data for any other purpose set forth in this Privacy Policy.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>12. Your Responsibilities</strong>
                    </p>
                </div>

                <div>
                    <p>
                        You are permitted, and hereby agree, to only provide personal information to us if such personal
                        information is accurate, reliable, and relevant to our relationship and only to the extent such
                        disclosure will not violate any applicable data protection law, statute, or regulation or infringe upon
                        any individual&apos;s data privacy rights or privileges. IF YOU PROVIDE US WITH ANY PERSONAL INFORMATION
                        ABOUT YOURSELF OR A THIRD PARTY, YOU EXPRESSLY REPRESENT AND WARRANT TO US THAT YOU HAVE THE FULL RIGHT
                        AND AUTHORITY TO DISCLOSE SUCH PERSONAL INFORMATION TO US, WE CAN USE SUCH PERSONAL INFORMATION IN
                        ACCORDANCE WITH THIS PRIVACY POLICY, AND YOU ARE IN COMPLIANCE WITH THE REQUIREMENTS SET FORTH HEREIN.
                        You also agree to fully reimburse us for any damages, losses, or expenses that arise based on your
                        violation of your representations, warranties and responsibilities set forth in this Privacy Policy.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>13. Email Marketing and Opt-Out Requests</strong>
                    </p>
                </div>

                <div>
                    <p>
                        You have the right to opt-out of receiving electronic direct marketing communications from us. All
                        electronic direct marketing communications that you may receive from us, such as e-mail messages, will
                        give you an &quot;unsubscribe&quot; option of not receiving such communications from us in the future.
                        California residents have the right to request certain information regarding our disclosure of personal
                        information to third parties for their direct marketing purposes. We do not share personal information
                        with third parties whom we have reason to believe use such information for their own direct marketing
                        purposes.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>14. Nevada Privacy Rights</strong>
                    </p>
                </div>

                <div>
                    <p>
                        Although we do not currently conduct sales of personal information, Nevada residents may submit a request
                        directing us to not sell their personal information that we maintain about them to third parties who will
                        sell or license their information to others.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>15. Persons with Disabilities</strong>
                    </p>
                </div>

                <div>
                    <p>
                        We strive to ensure that every person has access to information related to our products and services,
                        including this Privacy Policy. Please contact us if you would like this Privacy Policy provided in an
                        alternative format and we will seek to meet your needs.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>16. Events and Video Teleconferencing</strong>
                    </p>
                </div>

                <div>
                    <p>
                        We host and use video teleconferencing platforms to facilitate conferences, meetings, training events,
                        and other programs. We often use online platforms that are owned and administered by a third-party
                        service provider (e.g., Zoom, Cisco, or Microsoft). Please be aware that our video teleconferencing may
                        record the content, sessions, conversations, and discussions thereon, and such records may be stored or
                        retained by us or our third-party service providers. By participating in these events and video
                        teleconferencing, you hereby consent to the collection and retention of any information provided therein,
                        and hereby consent to the recording of such activities.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>17. Your California Privacy Rights</strong>
                    </p>
                </div>

                <div>
                    <p>
                        In this California Rights Privacy Statement (this &quot;Statement&quot;), we want you to be aware of how
                        we collect, use and share your personal information. This Statement covers information we collect online
                        through our Websites, and from third parties and information we collect offline, for example from our
                        neighbors, associates, vendors and investors. If you are a California resident, California law permits
                        you to request Information regarding the:
                    </p>
                </div>

                <div>
                    <div>
                        <ul>
                            <li>
                                <p>Categories of personal information collected, received, sold or disclosed by us;</p>
                            </li>
                            <li>
                                <p>Purposes for which categories of personal information collected by us are used;</p>
                            </li>
                            <li>
                                <p>Sources of information from which we collect personal information; and</p>
                            </li>
                            <li>
                                <p>Specific pieces of personal information we have collected about you.</p>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p>In addition, if you are a California resident you may:</p>
                    </div>
                </div>

                <div>
                    <div>
                        <ul>
                            <li>
                                <p>
                                    Opt-out of receiving marketing communications from us; however, you may still receive
                                    administrative communications regarding our services;
                                </p>
                            </li>
                            <li>
                                <p>
                                    Request deletion of your personal information by us and our service providers, in some
                                    circumstances. Please be aware that in many instances, the law allows us to retain your
                                    personal information for several lawful purposes.
                                </p>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p>
                            You will not be discriminated against for exercising your rights under the California Consumer
                            Privacy Act (&quot;CCPA&quot;). You may exercise these rights via one of the methods described below
                            under the section titled &quot;How to Make Requests&quot;.
                        </p>
                    </div>
                </div>

                <div>
                    <p>
                        <strong>Collection of Personal Information from California Residents</strong> <br />
                        We have listed below the personal information we may have collected from California residents in the past
                        12 months.
                    </p>
                </div>

                <div>
                    <table>
                        <tbody>
                            <tr className={styles.TableHead}>
                                <td colSpan={2}>
                                    <p>
                                        <span>Category of personal information we may collect: </span>
                                        <strong>
                                            <span>Identifiers</span>
                                        </strong>
                                        <span>
                                            (including, but not limited to, names, addresses, email address, IP addresses, cookie
                                            identifiers)
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                Categories of sources from which we may obtain your Identifiers include, but are
                                                not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Directly from individuals; public records from government entities; business to
                                            business companies; real estate data compiling companies; third-party verification
                                            companies; indirectly from individuals (e.g., use of our Websites); law enforcement;
                                            insurance companies; sellers of shopping centers; brokers; consultants and attorneys;
                                            title companies; environmental firms
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                The purposes for collecting your Identifiers include, but are not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Communications; marketing and advertising services; managing our relationship and
                                            your account; verifying customer information; qualifying individuals; fulfilling
                                            transactions and payments; acquisition due diligence; collections; system
                                            administration and security; shopping center management operations; purchasing
                                            analysis; project management administration; talent management and job applicant
                                            processing
                                        </span>
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p>
                        <span> </span>
                    </p>
                </div>

                <div>
                    <table>
                        <tbody>
                            <tr className={styles.TableHead}>
                                <td colSpan={2}>
                                    <p>
                                        <span>Category of personal information we may collect: </span>
                                        <strong>
                                            <span>Commercial Information</span>
                                        </strong>
                                        <span>(including, but not limited to, records of personal property)</span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                Categories of sources from which we may obtain your Commercial Information
                                                include, but are not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Directly from individuals (neighbors); business to business companies; service
                                            providers; commercial agreements
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                The purposes for collecting your Commercial Information include, but are not
                                                limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>Managing and servicing accounts; collections; system administration</span>
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p>
                        <span> </span>
                    </p>
                </div>

                <div>
                    <table>
                        <tbody>
                            <tr className={styles.TableHead}>
                                <td colSpan={2}>
                                    <p>
                                        <span>Category of personal information we may collect: </span>
                                        <strong>
                                            <span>Internet/Electronic Activity</span>
                                        </strong>
                                        <span>
                                            (including, but not limited to, browsing history, search history, or information
                                            regarding interaction with our Websites)
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                Categories of sources from which we may obtain your “Internet/Electronic
                                                Activity” include, but are not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Directly from individuals that of our Websites; tenant platform (e.g., Tenant)
                                            activity log
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                The purposes for collecting your “Internet/Electronic Activity” include, but are
                                                not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Internal marketing analytics; troubleshooting; analytics; system
                                            administration/security; billing; collections; talent management and job applicant
                                            processing
                                        </span>
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p>
                        <span> </span>
                    </p>
                </div>

                <div>
                    <table>
                        <tbody>
                            <tr className={styles.TableHead}>
                                <td colSpan={2}>
                                    <p>
                                        <span>Category of personal information we may collect: </span>
                                        <strong>
                                            <span>Personal Characteristics</span>
                                        </strong>
                                        <span>
                                            (including, but not limited to, for example, race, religion, gender identity, gender
                                            expression, marital status, military or veteran status, disability, etc.)
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                Categories of sources from which we may obtain your Personal Characteristics
                                                include, but are not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>Directly from individuals; photos/videos; leases; service contracts</span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                The purposes for collecting your Personal Characteristics include, but are not
                                                limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Internal marketing analytics; troubleshooting; analytics; system
                                            administration/security; billing; collections; claims management; shopping center
                                            management; purchasing analysis, due diligence; managing and servicing accounts;
                                            operations; talent management and job applicant processing.
                                        </span>
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p>
                        <span> </span>
                    </p>
                </div>

                <div>
                    <table>
                        <tbody>
                            <tr className={styles.TableHead}>
                                <td colSpan={2}>
                                    <p>
                                        <span>Category of personal information we may collect: </span>
                                        <strong>
                                            <span>Protected Identifiers</span>
                                        </strong>
                                        <span>
                                            (including, but not limited to, telephone numbers, signatures, insurance policy
                                            numbers, and financial information)
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                Categories of sources from which we may obtain your Protected Identifiers
                                                include, but are not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Directly from individuals; business to business companies;
                                            vendors/consultants/attorneys; surveyors; title companies, environmental firms;
                                            government and public records
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                The purposes for collecting your Protected Identifiers include, but are not
                                                limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Managing and servicing accounts, verifying customer information, fulfilling
                                            transactions; purchasing analysis; shopping center management; operations;
                                            communications; system administration; security
                                        </span>
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p>
                        <span> </span>
                    </p>
                </div>

                <div>
                    <table>
                        <tbody>
                            <tr className={styles.TableHead}>
                                <td colSpan={2}>
                                    <p>
                                        <span>Category of personal information we may collect: </span>
                                        <strong>
                                            <span>Video and Telephonic Recording Information</span>
                                        </strong>
                                        <span>
                                            (including, but not limited to, voice and video recordings and facial recognition)
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                Categories of sources from which we may obtain your Biometric Information
                                                include, but are not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Directly from individuals (voicemail; help desk); cameras at shopping centers
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                The purposes for collecting your Biometric Information include, but are not
                                                limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>Communications; troubleshooting; shopping center management; security</span>
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p>
                        <span> </span>
                    </p>
                </div>

                <div>
                    <table>
                        <tbody>
                            <tr className={styles.TableHead}>
                                <td colSpan={2}>
                                    <p>
                                        <span>Category of personal information we may collect: </span>
                                        <strong>
                                            <span>Professional or Employment Related Information</span>
                                        </strong>
                                        <span>(including, but not limited to, employer, dates of employment and job title)</span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                Categories of sources from which we may obtain your Professional or Employment
                                                Related Information include, but are not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>Directly from individuals</span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                The purposes for collecting your Professional or Employment Related Information
                                                include, but are not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Purchasing analysis; due diligence; operations; qualifying individuals; shopping
                                            center management; system administration; billing; collections; account
                                            administration; payments; talent management and job applicant processing
                                        </span>
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p>
                        <span> </span>
                    </p>
                </div>

                <div>
                    <table>
                        <tbody>
                            <tr className={styles.TableHead}>
                                <td colSpan={2}>
                                    <p>
                                        <span>Category of personal information we may collect: </span>
                                        <strong>
                                            <span>Education Information</span>
                                        </strong>
                                        <span>(including, but not limited to, schools attended and degrees obtained)</span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                Categories of sources from which we may obtain your Education Information
                                                include, but are not limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Directly from individuals, third party references, previous employers, background
                                            check providers.
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                The purposes for collecting your Education Information include, but are not
                                                limited to:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Purchasing analysis; due diligence; operations; shopping center management;
                                            qualifying individuals; shopping system administration; billing; collections; account
                                            administration; payments; talent management and job applicant processing
                                        </span>
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p>
                        <span> </span>
                    </p>
                </div>

                <div>
                    <p>Below is information on whether we sell or share your personal information.</p>
                </div>

                <div>
                    <table>
                        <tbody>
                            <tr className={styles.TableHead}>
                                <td colSpan={2}>
                                    <p>
                                        <strong>
                                            <span>Selling your personal information</span>
                                        </strong>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>Categories of third parties we may sell your personal information to:</span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            <strong>
                                                We do not sell personal information to third parties for profit or other valuable
                                                consideration.
                                            </strong>
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                Categories of third parties to whom information was disclosed for a business
                                                purpose:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Prospective purchasers; purchasers; brokers; vendors; consultants; lenders; joint
                                            venture partners; web developer; potential debt or equity sources; social networks,
                                            service providers; local municipalities; courts; insurance companies; adjusters;
                                            lease assignees; collection firms; audit firms; banks
                                        </span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.TableMainCol}>
                                    <p>
                                        <strong>
                                            <span>
                                                Business Purposes for sharing information with service providers or third
                                                parties:
                                            </span>
                                        </strong>
                                    </p>
                                    <p>
                                        <span> </span>
                                    </p>
                                </td>
                                <td>
                                    <p>
                                        <span>
                                            Marketing and advertising services; mail services; account management;
                                            communications; underwriting; identity verification; detecting security incidents;
                                            protecting against malicious, deceptive, fraudulent or illegal activity, and
                                            prosecuting those responsible for that activity; customer service; billing;
                                            collections; payments; lease administration; audit; legal compliance; valuations;
                                            system administration and security; loan compliance requirements; troubleshooting;
                                            product development and improvements; talent management and job applicant processing
                                        </span>
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p>
                        <span> </span>
                    </p>
                </div>

                <div>
                    <p>
                        <strong>How to Make Requests</strong> <br />
                        You can make Access or Deletion requests by the following method:
                    </p>
                </div>

                <div>
                    <p>
                        1. Email by emailing{' '}
                        <a href="mailto:support@idplans.com" target="_blank" rel="noreferrer noopener">
                            support@idplans.com
                        </a>
                    </p>
                </div>

                <div>
                    <p>
                        Please be aware that we do not accept or process requests through other means (e.g., via fax, social
                        media, etc.).
                    </p>
                </div>

                <div>
                    <p>
                        If you make, or an authorized agent on your behalf makes, any request related to your personal
                        information under California law, we will ascertain your identity (and the identity of the authorized
                        agent, to the extent applicable) to the degree of certainty required under the law before addressing your
                        request. We may require you to match at least two or three pieces of personal information we have
                        previously collected from you before granting you access to or deleting your personal information. We may
                        require written documentation that demonstrates a third party is authorized to serve as your agent for
                        the purposes of submitting the requests set forth herein. Please also be aware that making any such
                        request for the deletion of your personal information does not ensure complete deletion of the personal
                        information and there may be circumstances in which the law does not require or allow us to fulfill your
                        request.
                    </p>
                </div>

                <div>
                    <p>
                        <strong>18. How to Contact Us About this Privacy Policy</strong>
                    </p>
                </div>

                <div>
                    <p>
                        If you have any questions regarding this Privacy Policy, please email us at:{' '}
                        <a href="mailto:support@idplans.com" target="_blank" rel="noreferrer noopener">
                            support@idplans.com
                        </a>
                        .
                    </p>
                </div>

                <div>
                    <p>
                        <strong>19. Modifications to Privacy Policy</strong>
                    </p>
                </div>

                <div>
                    <p>
                        At any time, we reserve the right to add to, modify or remove portions of our Privacy Policy. The
                        effective date of our Privacy Policy will be included at the beginning of the Privacy Policy and will
                        serve as a notice of when changes have been implemented to the Privacy Policy. Please check back to this
                        page from time to time to determine if any revisions have been made. Your continued use of this Website
                        after any revisions have been made will indicate your acknowledgement of such changes and agreement to be
                        bound by the updated terms and conditions.
                    </p>
                </div>

                <div>
                    <p>Use of this Website constitutes your acceptance of our Privacy Policy.</p>
                </div>
            </section>
        </ExpandableSection>
    </PageWrapper>
);

export default PrivacyPolicyPage;
